import React, {useCallback} from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// Static sx objects hoisted to avoid recreation on every render
const HEADER_STACK_SX = {width: '100%', minHeight: 40, p: 1, cursor: 'pointer'};
const ICON_BOX_SX = {
    width: 26, height: 26, borderRadius: 999,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
};
const TITLE_STACK_SX = {flex: 1, minWidth: 0};
const SPINNER_BOX_SX = {display: 'flex', alignItems: 'center', px: 0.5};
const ACTION_BOX_SX = {display: 'flex', gap: 0.5};
const DELETE_BTN_SX = {width: 24, height: 24, padding: 0};

/**
 * Header component for the ReferenceMarker
 * Displays the icon, count, content preview, refresh and delete buttons
 */
const ReferenceMarkerHeader = React.memo(function ReferenceMarkerHeader({
    marker,
    isActive,
    onClick,
    onDelete,
    count,
    analysisLoading,
    claimsSummary,
    iconColor,
    iconBg,
    iconBorder,
    Icon,
}) {
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.(e);
        }
    }, [onClick]);

    const handleDelete = useCallback((e) => {
        e.stopPropagation();
        onDelete?.(marker);
    }, [onDelete, marker]);

    const totalClaims = claimsSummary?.total_claims ?? 0;
    const claimColor = claimsSummary?.contradicted_claims > 0 ? '#d32f2f'
        : (claimsSummary?.unproven_claims > 0 || claimsSummary?.partially_proven_claims > 0) ? '#ed6c02'
        : (claimsSummary?.proven_claims === totalClaims) ? '#2e7d32'
        : '#ed6c02';

    return (
        <Stack
            role="button"
            direction="row"
            alignItems="flex-start"
            gap={1}
            tabIndex={0}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            sx={HEADER_STACK_SX}
        >
            <Box sx={{...ICON_BOX_SX, bgcolor: iconBg, border: `1px solid ${iconBorder}`}}>
                {Icon ? (
                    <Icon fontSize="small" sx={{color: iconColor}}/>
                ) : null}
            </Box>
            <Stack sx={TITLE_STACK_SX}>
                <Typography variant="subtitle2"
                            sx={{fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                    {typeof count === 'number'
                        ? `${count} reference${count === 1 ? '' : 's'}`
                        : 'References'}
                    {(claimsSummary && totalClaims > 0) ? (
                        <span style={{
                            color: claimColor,
                            fontWeight: 400
                        }}>
                            ({claimsSummary.proven_claims}/{totalClaims} claim{totalClaims === 1 ? '' : 's'})
                        </span>
                    ) : null}
                    {(!isActive && analysisLoading) ? (
                        <Box sx={SPINNER_BOX_SX}>
                            <CircularProgress size={14} thickness={4}/>
                        </Box>
                    ) : null}
                </Typography>
                {marker?.contentPreview ? (
                    <Typography variant="caption" sx={{color: 'text.secondary'}} noWrap>
                        {marker.contentPreview}
                    </Typography>
                ) : null}
            </Stack>
            {isActive ? (
                <Box sx={ACTION_BOX_SX}>
                    {analysisLoading ? (
                        <Box sx={SPINNER_BOX_SX}>
                            <CircularProgress size={14} thickness={4}/>
                        </Box>
                    ) : null}
                    {onDelete ? (
                        <IconButton
                            size="small"
                            onClick={handleDelete}
                            title="Delete reference marker"
                            sx={DELETE_BTN_SX}
                        >
                            <DeleteOutlineIcon fontSize="small"/>
                        </IconButton>
                    ) : null}
                </Box>
            ) : null}
        </Stack>
    );
});

export default ReferenceMarkerHeader;
